<template>
  <div id="overdue-group">
    <table>
      <thead>
        <tr>
          <th>{{ $t('OVERDUE_GROUP') }}</th>
          <th>{{ $t('OVERDUE_CNTRCT') }}</th>
          <th>{{ $t('OUT_OF_REACH') }}</th>
          <th>{{ $t('TO_RENEW') }}</th>
          <th>{{ $t('OUTSTANDING_AMOUNT') }}</th>
          <th>{{ $t('LOAN_BALANCE') }}</th>
          <th>{{ $t('INTEREST_TOTAL') }}</th>
          <th>{{ $t('OVERDUE_FEE') }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr :key="index" v-for="(item, index) in lateDaysGroupItems">
          <td @click="() => swapToClientList(item.text)">{{ $t(item.text) }}</td>
          <td>{{ item.total }}</td>
          <td>{{ item.outOfReach }}</td>
          <td>{{ item.toRenew }}</td>
          <td>{{ item.totalOutstandingAmount }}</td>
          <td>{{ item.loanBalance }}</td>
          <td>{{ item.totalInterestAmount }}</td>
          <td>{{ item.totalOverdueAmount }}</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import { addCommas } from '@/helpers';

import { BIG_CLIENT } from '@/consts';

const { useState, useActions } = createNamespacedHelpers('paymentReminder');

export default {
  name: 'OverdueGroup',

  setup() {
    const { paymentReminderStatus } = useState(['paymentReminderStatus']);
    const { setCurrentComponent, setLateDays } = useActions(['setCurrentComponent', 'setLateDays']);

    const lateDaysGroupItems = computed(() => [
      {
        text: 'TODAY',
        total: paymentReminderStatus.value.today.total,
        toRenew: paymentReminderStatus.value.today.appointments,
        outOfReach: paymentReminderStatus.value.today.outOfReach,
        totalOutstandingAmount: addCommas(paymentReminderStatus.value.today.totalOutstandingAmount),
        loanBalance: addCommas(paymentReminderStatus.value.today.totalPrincipalAmount),
        totalInterestAmount: addCommas(paymentReminderStatus.value.today.totalInterestAmount),
        totalOverdueAmount: addCommas(paymentReminderStatus.value.today.totalOverdueAmount)
      },
      {
        text: '1_DAY',
        total: paymentReminderStatus.value.late1Day.total,
        toRenew: paymentReminderStatus.value.late1Day.appointments,
        outOfReach: paymentReminderStatus.value.late1Day.outOfReach,
        totalOutstandingAmount: addCommas(paymentReminderStatus.value.late1Day.totalOutstandingAmount),
        loanBalance: addCommas(paymentReminderStatus.value.late1Day.totalPrincipalAmount),
        totalInterestAmount: addCommas(paymentReminderStatus.value.late1Day.totalInterestAmount),
        totalOverdueAmount: addCommas(paymentReminderStatus.value.late1Day.totalOverdueAmount)
      },
      {
        text: '2_3_DAYS',
        total: paymentReminderStatus.value.late2To3Days.total,
        toRenew: paymentReminderStatus.value.late2To3Days.appointments,
        outOfReach: paymentReminderStatus.value.late2To3Days.outOfReach,
        totalOutstandingAmount: addCommas(paymentReminderStatus.value.late2To3Days.totalOutstandingAmount),
        loanBalance: addCommas(paymentReminderStatus.value.late2To3Days.totalPrincipalAmount),
        totalInterestAmount: addCommas(paymentReminderStatus.value.late2To3Days.totalInterestAmount),
        totalOverdueAmount: addCommas(paymentReminderStatus.value.late2To3Days.totalOverdueAmount)
      },
      {
        text: '4_5_DAYS',
        total: paymentReminderStatus.value.late4To5Days.total,
        toRenew: paymentReminderStatus.value.late4To5Days.appointments,
        outOfReach: paymentReminderStatus.value.late4To5Days.outOfReach,
        totalOutstandingAmount: addCommas(paymentReminderStatus.value.late4To5Days.totalOutstandingAmount),
        loanBalance: addCommas(paymentReminderStatus.value.late4To5Days.totalPrincipalAmount),
        totalInterestAmount: addCommas(paymentReminderStatus.value.late4To5Days.totalInterestAmount),
        totalOverdueAmount: addCommas(paymentReminderStatus.value.late4To5Days.totalOverdueAmount)
      },
      {
        text: 'OVER_5_DAYS',
        total: paymentReminderStatus.value.lateOver5Days.total,
        toRenew: paymentReminderStatus.value.lateOver5Days.appointments,
        outOfReach: paymentReminderStatus.value.lateOver5Days.outOfReach,
        totalOutstandingAmount: addCommas(paymentReminderStatus.value.lateOver5Days.totalOutstandingAmount),
        loanBalance: addCommas(paymentReminderStatus.value.lateOver5Days.totalPrincipalAmount),
        totalInterestAmount: addCommas(paymentReminderStatus.value.lateOver5Days.totalInterestAmount),
        totalOverdueAmount: addCommas(paymentReminderStatus.value.lateOver5Days.totalOverdueAmount)
      }
    ]);

    const swapToClientList = (lateDays) => {
      setLateDays(lateDays);
      setCurrentComponent(BIG_CLIENT);
    };

    return {
      lateDaysGroupItems,
      swapToClientList
    };
  },
};
</script>

<style lang='scss' scoped>
@import "~@/assets/scss/colors";
#overdue-group {
  table {
    width: 100%;
    text-align: center;
    border-collapse: separate;
    border-spacing: 0;

    thead {
      tr {
        text-transform: uppercase;
        font-weight: bold;
        font-family: 'Montserrat';
        padding: 20px 40px;
        font-size: 14px;
        color: $ebonyClay;

        th {
          border-bottom: 0px;
          background-color: $whisper;

          &:first-child {
            border-top-left-radius: 20px;
            border-left: 1px solid $geyser;
          }

          &:last-child {
            border-top-right-radius: 20px;
            border-right: 1px solid $geyser;
          }
        }
      }
    }

    tbody {
      color: $independence;

      tr {
        td {
          padding: 10px 15px;
          transition: all 0.5s ease-in-out;

          &:first-child {
            color: $blueNcs;
            border-left: 1px solid $geyser;
            text-decoration: underline;
            cursor: pointer;
          }

          &:first-child:hover {
            color: $paradisePink;
          }

          &:last-child {
            border-right: 1px solid $geyser;
          }
        }

        &:last-child {
          border-collapse: separate;

          td {
            border-bottom: 1px solid $geyser;

            &:first-child {
              border-bottom-left-radius: 20px;
            }

            &:last-child {
              border-bottom-right-radius: 20px;
            }
          }
        }
      }
    }
  }
}

</style>
